/**
 * Aggregation utilities for Hyperliquid data
 */

import type { PortfolioHistory, PortfolioHistoryKey } from './types';

/**
 * Aggregate portfolio history from multiple wallets
 * Sums values at each timestamp
 */
export function aggregatePortfolioHistory(
  walletsPortfolioHistory: Map<string, PortfolioHistory>
): PortfolioHistory {
  const aggregated: PortfolioHistory = {};

  const allKeys = new Set<PortfolioHistoryKey>();
  walletsPortfolioHistory.forEach((history) => {
    if (!history) return;
    (Object.keys(history) as PortfolioHistoryKey[]).forEach((key) =>
      allKeys.add(key)
    );
  });

  allKeys.forEach((key) => {
    const accountValueMap = new Map<number, number>();
    const pnlMap = new Map<number, number>();
    let totalVolume = 0;

    walletsPortfolioHistory.forEach((history) => {
      const series = history?.[key];
      if (!series) return;

      if (
        series.accountValueHistory &&
        Array.isArray(series.accountValueHistory)
      ) {
        series.accountValueHistory.forEach(([timestamp, value]) => {
          const numValue =
            typeof value === 'string' ? parseFloat(value) : value;
          const current = accountValueMap.get(timestamp) || 0;
          accountValueMap.set(timestamp, current + numValue);
        });
      }

      if (series.pnlHistory && Array.isArray(series.pnlHistory)) {
        series.pnlHistory.forEach(([timestamp, value]) => {
          const numValue =
            typeof value === 'string' ? parseFloat(value) : value;
          const current = pnlMap.get(timestamp) || 0;
          pnlMap.set(timestamp, current + numValue);
        });
      }

      if (series.vlm) {
        const vlmValue =
          typeof series.vlm === 'string' ? parseFloat(series.vlm) : series.vlm;
        totalVolume += vlmValue;
      }
    });

    const accountValueHistory: Array<[number, string]> = [];
    accountValueMap.forEach((value, timestamp) => {
      accountValueHistory.push([timestamp, value.toString()]);
    });
    accountValueHistory.sort((a, b) => a[0] - b[0]);

    const pnlHistory: Array<[number, string]> = [];
    pnlMap.forEach((value, timestamp) => {
      pnlHistory.push([timestamp, value.toString()]);
    });
    pnlHistory.sort((a, b) => a[0] - b[0]);

    if (accountValueHistory.length > 0 || pnlHistory.length > 0) {
      aggregated[key] = {
        accountValueHistory,
        pnlHistory,
        vlm: totalVolume.toString(),
      };
    }
  });

  return aggregated;
}
